import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  MessageSquare, Bot, Users, UserPlus, BarChart3, CreditCard, Settings as SettingsIcon,
  LogOut, Sparkles, AlertCircle, Crown, IndianRupee
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import PaywallModal from "@/components/PaywallModal";

const NAV = [
  { to: "/app/automations", icon: Bot, label: "AI Agents" },
  { to: "/app/conversations", icon: MessageSquare, label: "Conversations" },
  { to: "/app/leads", icon: UserPlus, label: "Leads" },
  { to: "/app/customers", icon: Users, label: "Customers" },
  { to: "/app/payments", icon: IndianRupee, label: "Payments" },
  { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/app/billing", icon: CreditCard, label: "Billing" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    api.get("/billing/me").then((r) => {
      setBilling(r.data);
      if (r.data.expired) setPaywallOpen(true);
    }).catch(() => {});
  }, [loc.pathname]);

  // Intercept 402 globally
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 402) setPaywallOpen(true);
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex" data-testid="app-shell">
      <aside className="hidden md:flex w-64 shrink-0 border-r border-white/10 bg-zinc-950/40 flex-col">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2" data-testid="app-logo">
            <Logo />
          </Link>
        </div>
        <div className="px-3 pt-4 pb-2">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 px-3 mb-1">Workspace</div>
          <div className="px-3 py-2 rounded-md bg-white/5 border border-white/10">
            <div className="text-sm font-medium truncate">{user?.business_name || "My Business"}</div>
            <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
        <nav className="px-3 mt-2 flex-1 space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
          {user?.is_super_admin && (
            <NavLink to="/app/admin" data-testid="nav-admin"
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors mt-3 border-t border-white/5 pt-3 ${isActive ? "bg-amber-500/10 text-amber-300" : "text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/5"}`}>
              <Crown className="w-4 h-4"/> Founder Console
            </NavLink>
          )}
        </nav>
        {billing && !billing.expired && billing.is_trial && (
          <div className="m-3 p-3 rounded-lg bg-emerald-500/[0.05] border border-emerald-500/20">
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300 mb-1">Trial</div>
            <div className="text-sm">{billing.days_left} days left</div>
            <button onClick={() => nav("/app/billing")} className="mt-2 text-xs text-emerald-300 hover:text-emerald-200" data-testid="trial-upgrade-link">Upgrade →</button>
          </div>
        )}
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-white/5" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 glass sticky top-0 z-30">
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="font-mono capitalize">{billing?.plan || user?.plan} plan</span>
            {billing?.is_trial && billing?.days_left !== undefined && (
              <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                {billing.days_left}d trial left
              </span>
            )}
          </div>
          {billing?.expired && (
            <div className="flex items-center gap-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4"/> Subscription expired
              <button onClick={() => setPaywallOpen(true)} className="ml-2 px-3 py-1 rounded-md bg-white text-black text-xs font-medium" data-testid="header-renew-btn">Renew</button>
            </div>
          )}
        </header>
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </main>

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} onSuccess={() => { setPaywallOpen(false); api.get("/billing/me").then(r => setBilling(r.data)); }} expired={billing?.expired} />
    </div>
  );
}

function Logo() {
  return (
    <>
      <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
        <span className="font-display font-bold text-black text-sm">i</span>
      </div>
      <span className="font-display tracking-tight text-lg">ipnova<span className="text-emerald-400">.ai</span></span>
    </>
  );
}

export { Logo };
